import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LeadFormFields } from "@/components/LeadFormFields";
import type { CatalogFieldsValues } from "@/components/CatalogFields";
import { useCreateLead } from "@/hooks/useLeads";
import { useCatalogContext } from "@/hooks/useCatalog";
import { useCrmStore } from "@/stores/crm.store";

type FormValues = {
  name: string;
  phone: string;
  email: string;
};

type FormErrors = {
  name?: string;
  phone?: string;
  email?: string;
};

function validate(values: FormValues): FormErrors {
  const errors: FormErrors = {};
  const phone = values.phone.trim();
  if (phone && phone.length < 8)
    errors.phone = "El teléfono debe tener al menos 8 caracteres";
  if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
    errors.email = "El correo no tiene un formato válido";
  return errors;
}

const EMPTY: FormValues = { name: "", phone: "", email: "" };
const EMPTY_CATALOG: CatalogFieldsValues = {
  cycleId: undefined,
  productId: undefined,
  capturedFields: {},
};

export function CreateLeadModal(): React.JSX.Element {
  const isOpen = useCrmStore((s) => s.isCreateModalOpen);
  const close = useCrmStore((s) => s.closeCreateModal);
  const createLead = useCreateLead();
  const { data: catalogContext } = useCatalogContext();

  const [values, setValues] = useState<FormValues>(EMPTY);
  const [catalogValues, setCatalogValues] = useState<CatalogFieldsValues>(EMPTY_CATALOG);
  const [errors, setErrors] = useState<FormErrors>({});

  function handleChange(field: keyof FormValues, value: string): void {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function handleOpenChange(open: boolean): void {
    if (!open) {
      close();
      setValues(EMPTY);
      setCatalogValues(EMPTY_CATALOG);
      setErrors({});
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const errs = validate(values);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    await createLead.mutateAsync(
      {
        name: values.name.trim() || undefined,
        phone: values.phone.trim() || undefined,
        email: values.email.trim() || undefined,
        ...(catalogValues.productId ? { productId: catalogValues.productId } : {}),
        ...(catalogValues.cycleId ? { cycleId: catalogValues.cycleId } : {}),
        ...(Object.keys(catalogValues.capturedFields).length > 0
          ? { capturedFields: catalogValues.capturedFields }
          : {}),
      },
      {
        onSuccess: () => {
          toast.success("Lead creado correctamente");
          handleOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.message ?? "Error al crear el lead");
        },
      },
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Nuevo lead</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-1">
          <form id="create-lead-form" onSubmit={(e) => void handleSubmit(e)}>
            <LeadFormFields
              values={values}
              errors={errors}
              onChange={handleChange}
              disabled={createLead.isPending}
              catalogContext={catalogContext}
              catalogValues={catalogValues}
              onCatalogChange={setCatalogValues}
            />
          </form>
        </div>

        <DialogFooter className="mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={createLead.isPending}
          >
            Cancelar
          </Button>
          <Button type="submit" form="create-lead-form" disabled={createLead.isPending}>
            {createLead.isPending ? "Guardando…" : "Crear lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
